import { Injectable, inject } from '@angular/core';
import { merge } from 'rxjs';
import { map } from 'rxjs/operators';

import { AppConfigService } from '../../../../common/config/app.config.service';
import { LogFactory } from '../../../../common/services/logger/log-factory';
import { NativeAudioMixer } from '../../../core/native-audio/native-audio-mixer.service';
import { NativeCardCommand } from '../../../core/native-audio/native-card';
import { MediaService } from '../../../core/service/media.service';
import { MediaStoreService } from '../../../core/service/media/media-store.service';
import { AppVisibilityService } from '../../../core/visibility/app-visibility.service';
import { WorkoutLockScreenCopy, WorkoutRemoteCommand } from '../enums/workout-player.enum';
import { BackgroundPlaybackService } from './background-playback.service';
import { DbService } from './db.service';
import { PauseReason, WorkoutService, WorkoutViewModel } from './workout.service';

/** How far the lock-screen side buttons move the workout, as the player's own ±10 buttons do. */
const SKIP_SECONDS = 10;
/** A card slider further than this from where the workout is gets moved. */
const CARD_DRIFT_TOLERANCE_MS = 1500;
const MS_PER_SECOND = 1000;
const HTTPS_SCHEME = 'https:';

const CARD_COMMANDS: Readonly<Record<NativeCardCommand, WorkoutRemoteCommand>> = {
  play: WorkoutRemoteCommand.PLAY,
  pause: WorkoutRemoteCommand.PAUSE,
  next: WorkoutRemoteCommand.NEXT,
  previous: WorkoutRemoteCommand.PREVIOUS,
  skipForward: WorkoutRemoteCommand.SKIP_FORWARD,
  skipBackward: WorkoutRemoteCommand.SKIP_BACKWARD,
};

/** The item the card stands for: an activity (its sets, reps and the rests between them), or the rest after one. */
interface CardStep {
  key: string;
  totalMs: number;
}

/**
 * The workout as the phone shows it outside the app (ADR-0284, ADR-0285,
 * ADR-0287).
 *
 * - **Lock screen, both platforms**: the card names the activity (or the
 *   rest, and what is up next), shows its cover, and its slider is the
 *   activity's time - held still while the workout is paused. Play / pause,
 *   one press of a side button (±10 s) and two (previous / next activity)
 *   drive the workout. It is up from the workout's start, sound or not.
 * - **A YouTube step with the app out of sight** cannot play (YouTube's terms
 *   allow no background play): the workout pauses, the card offers no Play -
 *   a press explains why in a notification - and Next / Previous still work.
 * - **Android without native audio**: the plain notification names the
 *   activity; Pause / Resume, Next and Stop drive the workout.
 *
 * Only real changes reach the phone: a new step, a pause or resume, or a jump
 * (±10 s). The slider runs on by itself between them.
 */
@Injectable({ providedIn: 'root' })
export class WorkoutRemoteControlService {
  private readonly workout = inject(WorkoutService);
  private readonly background = inject(BackgroundPlaybackService);
  private readonly mixer = inject(NativeAudioMixer);
  private readonly db = inject(DbService);
  private readonly store = inject(MediaStoreService);
  private readonly media = inject(MediaService);
  private readonly visibility = inject(AppVisibilityService);
  private readonly appName = inject(AppConfigService).appName;
  private readonly logger = inject(LogFactory).createLogger(WorkoutRemoteControlService);

  private started = false;
  private step: CardStep | null = null;
  /** What the card was last told, and when. */
  private sent: {
    playing: boolean;
    blocked: boolean;
    hasPrevious: boolean;
    hasNext: boolean;
    elapsedMs: number;
    at: number;
  } | null = null;
  /** The workout followed last, so a visibility change can re-send it. */
  private lastVm: WorkoutViewModel | null = null;
  /** What a lock-screen Play resumes natively, snapshotted as the workout paused. */
  private resumeSources: string[] = [];
  /** The step whose name and picture the card shows (they are read asynchronously). */
  private metadataKey: string | null = null;

  /** Starts following the workout; called once at start-up. */
  start(): void {
    if (this.started) return;
    this.started = true;
    merge(
      this.background.commands$,
      this.mixer.commands$.pipe(map((command) => CARD_COMMANDS[command])),
    ).subscribe((command) => void this.run(command));
    this.workout.workout$.subscribe((vm) => this.follow(vm));
    // Out of sight (app hidden or player minimised) a YouTube step's Play is
    // blocked, and in sight it is not.
    merge(this.visibility.visible$, this.workout.playerShown$).subscribe(() => {
      if (this.lastVm) this.follow(this.lastVm);
    });
  }

  private async run(command: WorkoutRemoteCommand): Promise<void> {
    try {
      switch (command) {
        case WorkoutRemoteCommand.PLAY:
          await this.workout.resume();
          return;
        case WorkoutRemoteCommand.PAUSE:
          await this.workout.pause(PauseReason.USER);
          return;
        case WorkoutRemoteCommand.NEXT:
          await this.workout.skipToNext();
          return;
        case WorkoutRemoteCommand.PREVIOUS:
          await this.workout.skipToPrev();
          return;
        case WorkoutRemoteCommand.SKIP_FORWARD:
          this.workout.requestNudge(SKIP_SECONDS);
          return;
        case WorkoutRemoteCommand.SKIP_BACKWARD:
          this.workout.requestNudge(-SKIP_SECONDS);
          return;
        case WorkoutRemoteCommand.STOP:
          await this.workout.stop();
          return;
      }
    } catch (err) {
      this.logger.error('A lock-screen or notification command failed', err as Error, { command });
    }
  }

  private follow(vm: WorkoutViewModel | null): void {
    const opened = !this.lastVm && !!vm;
    this.lastVm = vm;
    if (!vm) {
      this.step = null;
      this.sent = null;
      this.resumeSources = [];
      this.metadataKey = null;
      return;
    }
    const playing = !vm.isPaused || vm.isStarting;
    this.background.updateNotification({
      title: vm.label,
      paused: !playing,
      hasNext: !!vm.activityQueue?.length,
    });
    if (!this.mixer.enabled) return;
    if (opened) void this.mixer.ensureCard();
    this.followOnCard(vm, playing);
  }

  private followOnCard(vm: WorkoutViewModel, playing: boolean): void {
    const restAfter = !!vm.isRest && vm.restResumeSetIndex == null;
    const position = vm.activityHistory?.length ?? 0;
    const key = restAfter ? `rest|${position}` : `activity|${position}|${vm.activityId}`;
    const remainingMs = restAfter ? vm.remainingMs : vm.activityRemainingMs;
    const newStep = this.step?.key !== key;
    if (newStep || !this.step) this.step = { key, totalMs: remainingMs };
    // A rewind across a boundary, or a speed change, can make more of it left than it started with.
    this.step.totalMs = Math.max(this.step.totalMs, remainingMs);
    const elapsedMs = this.step.totalMs - remainingMs;

    if (this.sent?.playing && !playing) {
      // Taken as the workout pauses, while its music and track are still fading
      // out: what was sounding is what a lock-screen Play should bring back.
      this.resumeSources = this.mixer.resumableChannelIds();
    }
    const expectedMs = this.sent
      ? this.sent.elapsedMs + (this.sent.playing ? Date.now() - this.sent.at : 0)
      : 0;
    const moved = Math.abs(elapsedMs - expectedMs) > CARD_DRIFT_TOLERANCE_MS;
    const blocked = !!vm.isYoutubeVideo && !this.workout.videoOnScreen;
    const hasPrevious = WorkoutService.hasPreviousActivity(vm);
    const hasNext = WorkoutService.hasNextActivity(vm);
    const sent = this.sent;
    const changed =
      !sent ||
      sent.playing !== playing ||
      sent.blocked !== blocked ||
      sent.hasPrevious !== hasPrevious ||
      sent.hasNext !== hasNext;
    if (newStep || changed || moved) {
      this.sent = { playing, blocked, hasPrevious, hasNext, elapsedMs, at: Date.now() };
      this.mixer.setCardTimeline({
        playing,
        elapsed: elapsedMs / MS_PER_SECOND,
        duration: this.step.totalMs / MS_PER_SECOND,
        resumeSources: playing ? [] : this.resumeSources,
        hasPrevious,
        hasNext,
        playBlocked: blocked,
        ...(blocked
          ? {
              blockedNotice: this.visibility.isVisible()
                ? {
                    title: WorkoutLockScreenCopy.YOUTUBE_MINIMIZED_TITLE,
                    message: WorkoutLockScreenCopy.YOUTUBE_MINIMIZED_MESSAGE,
                  }
                : {
                    title: WorkoutLockScreenCopy.YOUTUBE_BLOCKED_TITLE,
                    message: WorkoutLockScreenCopy.YOUTUBE_BLOCKED_MESSAGE,
                  },
            }
          : {}),
      });
    }
    if (key !== this.metadataKey) {
      this.metadataKey = key;
      void this.updateCardMetadata(vm, key, restAfter);
    }
  }

  /** The card's title, subtitle and picture for a step; read from the library, so after the fact. */
  private async updateCardMetadata(vm: WorkoutViewModel, key: string, restAfter: boolean): Promise<void> {
    try {
      const next = vm.activityQueue?.[0];
      const nextActivity = restAfter && next ? await this.db.getActivity(next.activityId) : undefined;
      const activity = restAfter ? nextActivity : await this.db.getActivity(vm.activityId);
      const container = vm.contextSequenceId
        ? await this.db.getSequence(vm.contextSequenceId)
        : vm.contextGroupId
          ? await this.db.getGroup(vm.contextGroupId)
          : undefined;
      const artist = restAfter
        ? nextActivity
          ? `${WorkoutLockScreenCopy.NEXT_UP} ${nextActivity.name}`
          : this.appName
        : container?.name?.trim() || this.appName;
      const artworkUrl =
        (await this.pictureFor(activity?.imageMediaId)) || (await this.pictureFor(container?.imageMediaId));
      // The workout moved on while this was being read.
      if (this.metadataKey !== key) return;
      this.mixer.setCardMetadata({
        // "Rest" during a rest: the player labels it so.
        title: vm.label,
        artist,
        artworkUrl,
      });
    } catch (err) {
      this.logger.debug('Lock-screen card details unavailable', { reason: (err as Error)?.message });
    }
  }

  /**
   * A picture the device can show without signing in: the file on this device,
   * or a public catalog URL. A private cloud picture would need the app's
   * credentials, which the lock screen does not have.
   */
  private async pictureFor(mediaId: string | undefined): Promise<string> {
    if (!mediaId) return '';
    const file = await this.store.fileUriFor(mediaId);
    if (file) return file;
    const remote = (await this.db.getMediaMeta(mediaId))?.remoteUrl?.trim() ?? '';
    return remote.startsWith(HTTPS_SCHEME) && !this.media.isApiMediaUrl(remote) ? remote : '';
  }
}
